import React, { ChangeEvent, useRef } from "react";
import RequiredFieldIndicator from "@components/ui/indicators/RequiredFieldIndicator";
import { useTranslation } from "react-i18next";
import { t_help } from "@i18n/usualTexts/t_help";

export interface FormCheckBoxProps {
  name: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  value: string;
  immediateValidation?: boolean;
  errors?: Set<string>;
  required?: boolean;
  submitted?: boolean;
  toPresentErrors?: boolean;
  children: React.ReactNode;
  termsLink?: string;
  onTermsClick?: () => void;
  privacyPolicyLink?: string;
  onPrivacyPolicyClick?: () => void;
}

const LINK_BUTTON_CLASS = "p-0 underline text-[var(--accent-primary)] border-0 bg-transparent cursor-pointer";

function validateCheckbox(
  errors: Set<string>,
  value: string,
  required: boolean,
  submitted: boolean,
  requiredErrorMessage: string
): void {
  if (!required) {
    errors.delete(requiredErrorMessage);
    return;
  }
  if (value === "true") {
    errors.delete(requiredErrorMessage);
    return;
  }
  if (submitted) {
    errors.clear();
    errors.add(requiredErrorMessage);
  }
}

const CheckboxLinkButton: React.FC<{
  prefix: string;
  label: string;
  onClick?: () => void;
}> = ({ prefix, label, onClick }) => (
  <>
    {prefix}
    <button type="button" className={LINK_BUTTON_CLASS} onClick={onClick}>
      {label}
    </button>
  </>
);

const CheckboxErrors: React.FC<{
  name: string;
  errors: Set<string>;
  submitted: boolean;
  toPresentErrors: boolean;
}> = ({ name, errors, submitted, toPresentErrors }) => {
  if (errors.size === 0 || !toPresentErrors) return null;
  return (
    <>
      {[...errors].map((error) => (
        <div
          key={error}
          id={`${name}-error`}
          data-testid="spanElement"
          className={`text-xs text-red-500 ${submitted ? "animate-pulse" : ""}`}
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      ))}
    </>
  );
};

const TermsLinkSection: React.FC<{
  termsLink?: string;
  onTermsClick?: () => void;
  privacyPolicyLink?: string;
  onPrivacyPolicyClick?: () => void;
}> = ({ termsLink, onTermsClick, privacyPolicyLink, onPrivacyPolicyClick }) => {
  const { t } = useTranslation();
  return (
    <>
      {termsLink && <CheckboxLinkButton prefix=" " label={termsLink} onClick={onTermsClick} />}
      {privacyPolicyLink && <CheckboxLinkButton prefix={` ${t("help.and")} `} label={privacyPolicyLink} onClick={onPrivacyPolicyClick} />}
    </>
  );
};

const CheckIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M10 3L4.5 8.5L2 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function computeCheckboxState(errors: Set<string>, submitted: boolean, name: string) {
  const hasErrors = errors.size > 0;
  const hasErrorsAndSubmitted = hasErrors && submitted;
  const shakeClass = hasErrorsAndSubmitted ? " animate-pulse" : "";
  const errorDescribedBy = hasErrors ? `${name}-error` : undefined;
  return { shakeClass, errorDescribedBy };
}

function resolveCheckboxFlags(props: FormCheckBoxProps) {
  return {
    immediateValidation: props.immediateValidation ?? true,
    required: props.required ?? false,
    submitted: props.submitted ?? false,
    toPresentErrors: props.toPresentErrors ?? true,
  };
}

function resolveCheckboxValidation(props: FormCheckBoxProps) {
  return {
    errors: props.errors ?? new Set<string>(),
    ...resolveCheckboxFlags(props),
  };
}

function resolveCheckboxProps(props: FormCheckBoxProps) {
  return {
    name: props.name,
    onChange: props.onChange,
    value: props.value,
    children: props.children,
    termsLink: props.termsLink,
    onTermsClick: props.onTermsClick,
    privacyPolicyLink: props.privacyPolicyLink,
    onPrivacyPolicyClick: props.onPrivacyPolicyClick,
    ...resolveCheckboxValidation(props),
  };
}

const FormCheckBox: React.FC<FormCheckBoxProps> = (props) => {
  const { name, onChange, value, immediateValidation, errors, required, submitted, toPresentErrors, children, termsLink, onTermsClick, privacyPolicyLink, onPrivacyPolicyClick } = resolveCheckboxProps(props);
  const { t } = useTranslation();
  const requiredErrorMessage = t(t_help.check);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const isChecked = value === "true";

  validateCheckbox(errors, value, required, submitted, requiredErrorMessage);

  const { shakeClass, errorDescribedBy } = computeCheckboxState(errors, submitted, name);

  function handleToggle() {
    if (onChange && hiddenInputRef.current) {
      const syntheticEvent = {
        target: {
          name,
          checked: !isChecked,
          type: "checkbox",
        },
        currentTarget: {
          name,
          checked: !isChecked,
          type: "checkbox",
        },
        preventDefault: () => {},
        stopPropagation: () => {},
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  }

  return (
    <div className={`flex flex-col gap-1${shakeClass}`}>
      <div className="flex items-start gap-2">
        <input
          ref={hiddenInputRef}
          type="checkbox"
          className="sr-only"
          data-testid={name}
          name={name}
          id={name}
          onChange={onChange}
          data-immediate-validation={immediateValidation}
          checked={isChecked}
          readOnly
          aria-describedby={errorDescribedBy}
        />
        <button
          type="button"
          role="checkbox"
          aria-checked={isChecked}
          aria-labelledby={`${name}-label`}
          onClick={handleToggle}
          className={[
            "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2",
            "transition-all duration-200",
            "focus:outline-none focus:ring-2 focus:ring-[var(--checkbox-bg-checked)] focus:ring-offset-1",
            "hover:border-[var(--checkbox-border-hover)] hover:cursor-pointer",
            isChecked
              ? "border-transparent bg-[var(--checkbox-bg-checked)] text-[var(--checkbox-icon-color)] hover:bg-[var(--checkbox-bg-checked-hover)]"
              : "border-[var(--checkbox-border)] bg-[var(--checkbox-bg)]",
            errors.size > 0 && !isChecked
              ? "border-[var(--checkbox-bg-checked)] shadow-[0_0_0_1px_var(--checkbox-bg-checked)]"
              : "",
          ].filter(Boolean).join(" ")}
        >
          {isChecked && <CheckIcon />}
        </button>

        <label id={`${name}-label`} htmlFor={name} className="text-sm leading-relaxed break-words text-[var(--checkbox-label-color)]">
          {children}
          <TermsLinkSection
            termsLink={termsLink}
            onTermsClick={onTermsClick}
            privacyPolicyLink={privacyPolicyLink}
            onPrivacyPolicyClick={onPrivacyPolicyClick}
          />
          <RequiredFieldIndicator required={required} className="ml-0.5" />
        </label>
      </div>

      <CheckboxErrors name={name} errors={errors} submitted={submitted} toPresentErrors={toPresentErrors} />
    </div>
  );
};

export default FormCheckBox;
